import React from "react";
import { useI18n } from "../context/I18nContext";
import { Reveal } from "../components/shared";
import { InquiryForm } from "../components/InquiryForm";
import { Button } from "../components/ui/button";
import { Sun, Palette, Sprout, ChefHat, Waves, Music, Check } from "lucide-react";

const CAMP_HERO = "https://images.unsplash.com/photo-1560130958-0ea787c275de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const weeks = [
  { icon: Palette, name: "Little Artists", desc: "A week in the Atelier — paint, clay, and big imaginations." },
  { icon: Sprout, name: "Garden Explorers", desc: "Planting, digging, bugs, and harvesting our own snacks." },
  { icon: ChefHat, name: "Tiny Chefs", desc: "Hands-on cooking adventures from around the world." },
  { icon: Waves, name: "Water & Wonder", desc: "Splash play, science, and sensory outdoor fun." },
  { icon: Music, name: "Music & Movement", desc: "Dance, instruments, and joyful expression." },
  { icon: Sun, name: "Nature Adventurers", desc: "Nature walks, forest games, and outdoor discovery." },
];

export default function SummerCamp() {
  const { t } = useI18n();
  return (
    <div>
      <section className="relative min-h-[70vh] flex items-end">
        <img src={CAMP_HERO} alt="Summer camp" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 pb-16 pt-40 w-full">
          <Reveal>
            <span className="inline-block px-4 py-1.5 rounded-full bg-mustard text-navy text-sm font-semibold mb-5">Ages 3 – 8 · Registration Open</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight max-w-3xl drop-shadow-md">Summer Camp at Ainalem</h1>
            <p className="mt-5 text-lg text-white/90 max-w-xl drop-shadow">Weekly themed adventures full of art, nature, cooking, and outdoor play. Sign up for one week or all summer long!</p>
            <div className="mt-8">
              <InquiryForm type="summer_camp" trigger={<Button size="lg" className="rounded-full bg-coral hover:bg-coral-dark text-white h-13 px-8 text-base shadow-lg" data-testid="camp-hero-inquire-btn">{t.form.camp}</Button>} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <Reveal>
          <div className="text-coral font-semibold tracking-widest uppercase text-xs mb-3">Weekly themes</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">Six weeks of summer magic</h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {weeks.map((w, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="bg-white rounded-3xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.06)] h-full" data-testid={`camp-week-${i}`}>
                <div className="h-12 w-12 rounded-2xl bg-coral/10 flex items-center justify-center mb-4"><w.icon className="h-6 w-6 text-coral" /></div>
                <div className="text-xs font-semibold text-mustard-dark uppercase tracking-wider">Week {i + 1}</div>
                <h3 className="font-heading text-xl font-bold text-navy mt-1">{w.name}</h3>
                <p className="text-navy/60 mt-2 leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <h2 className="font-heading text-3xl font-bold text-navy">What's included</h2>
            <ul className="mt-6 space-y-3">
              {["Daily art, garden & cooking activities", "Fresh, healthy lunch and snacks", "Small groups with caring educators", "Weekly field trips & special guests", "Flexible weekly enrollment", "Extended care available"].map((x, i) => (
                <li key={i} className="flex gap-3 text-navy/70"><Check className="h-5 w-5 text-emerald-500 shrink-0" />{x}</li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="bg-cream rounded-3xl p-8 border border-black/5 text-center">
              <div className="text-navy/60">Per week, per child</div>
              <div className="font-heading text-5xl font-extrabold text-navy mt-2">$325</div>
              <div className="text-sm text-navy/50 mt-1">Sibling & multi-week discounts available</div>
              <InquiryForm type="summer_camp" trigger={<Button className="mt-6 w-full rounded-full bg-coral hover:bg-coral-dark text-white h-12" data-testid="camp-apply-btn">Apply for Summer Camp</Button>} />
              <p className="text-xs text-navy/40 mt-3">Enrolled families can pay the camp fee securely in the Parent Portal.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
